export const PREMIUM_ICON_CLASS = 'bi-stars'
export const PREMIUM_ACCESS_DOC_PATH = ['settings', 'premiumAccess']
export const MONTHLY_PREMIUM_PRICE = 0.5
export const YEARLY_PREMIUM_PRICE = 5
export const YEARLY_PREMIUM_MONTHS = 12

export const normalizePremiumEmail = email =>
  String(email || '').trim().toLowerCase()

export const isValidPremiumEmail = email => {
  const normalizedEmail = normalizePremiumEmail(email)
  if (!normalizedEmail) return false

  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)
}

export const normalizePremiumUid = uid =>
  String(uid || '').trim()

const toDate = value => {
  if (!value) return null
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }
  if (typeof value?.toDate === 'function') return value.toDate()
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000)

  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const normalizePremiumAmount = value => {
  const normalized = Number(String(value || '').replace(',', '.'))
  if (!Number.isFinite(normalized) || normalized <= 0) return 0
  return Number(normalized.toFixed(2))
}

export const formatPremiumPrice = amount =>
  new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(normalizePremiumAmount(amount))

export const formatPremiumDate = value => {
  const date = toDate(value)
  return date ? date.toLocaleDateString('it-IT') : 'Senza scadenza'
}

export const calculatePremiumMonthsFromAmount = amount => {
  const cents = Math.round(normalizePremiumAmount(amount) * 100)
  if (cents <= 0) return 0

  const monthlyCents = Math.round(MONTHLY_PREMIUM_PRICE * 100)
  const yearlyCents = Math.round(YEARLY_PREMIUM_PRICE * 100)
  const annualBlocks = Math.floor(cents / yearlyCents)
  const remainingCents = cents % yearlyCents
  const extraMonths = Math.floor(remainingCents / monthlyCents)

  return annualBlocks * YEARLY_PREMIUM_MONTHS + extraMonths
}

export const addPremiumMonths = (baseDate = new Date(), months = 0) => {
  const date = toDate(baseDate) || new Date()
  const nextDate = new Date(date)
  nextDate.setMonth(nextDate.getMonth() + Math.max(0, Number(months) || 0))
  nextDate.setHours(23, 59, 59, 999)
  return nextDate
}

export const calculatePremiumExpiryDate = ({
  amount,
  startDate = new Date()
} = {}) => {
  const months = calculatePremiumMonthsFromAmount(amount)
  return months > 0 ? addPremiumMonths(startDate, months) : null
}

export const isPremiumEntryExpired = (entry = {}, now = new Date()) => {
  const expiresAt = toDate(entry.expiresAt)
  if (!expiresAt) return false
  return expiresAt.getTime() < now.getTime()
}

export const isPremiumEntryActive = (entry = {}, now = new Date()) =>
  entry.active !== false && !isPremiumEntryExpired(entry, now)

const normalizePremiumEntry = (entry = {}, index = 0) => {
  const email = normalizePremiumEmail(entry.email)
  const uid = normalizePremiumUid(entry.uid)
  const paidAmount = normalizePremiumAmount(entry.paidAmount)
  const paidMonths = Number(entry.paidMonths) || calculatePremiumMonthsFromAmount(paidAmount)
  const expiresAt = toDate(entry.expiresAt)

  return {
    id: entry.id || uid || email || `premium-${index}`,
    email,
    uid,
    label: String(entry.label || entry.username || entry.displayName || '').trim(),
    active: entry.active !== false,
    createdAt: entry.createdAt || null,
    paidAmount,
    paidMonths,
    expiresAt: expiresAt ? expiresAt.toISOString() : null,
    lastPaymentAt: entry.lastPaymentAt || null
  }
}

export const normalizePremiumEntries = entries =>
  (Array.isArray(entries) ? entries : [])
    .map(normalizePremiumEntry)
    .filter(entry => entry.email || entry.uid)

export const isPremiumUser = (user, entries = [], now = new Date()) => {
  if (!user) return false

  const userEmail = normalizePremiumEmail(user.email)
  const userUid = normalizePremiumUid(user.uid)

  return normalizePremiumEntries(entries).some(entry => {
    if (!isPremiumEntryActive(entry, now)) return false
    return Boolean(
      (entry.email && entry.email === userEmail) ||
        (entry.uid && entry.uid === userUid)
    )
  })
}

export const createPremiumEntry = ({
  email = '',
  uid = '',
  label = '',
  active = true,
  createdAt = new Date().toISOString(),
  paidAmount = 0,
  paidMonths = calculatePremiumMonthsFromAmount(paidAmount),
  expiresAt = null,
  lastPaymentAt = new Date().toISOString()
} = {}) => ({
  id: normalizePremiumUid(uid) || normalizePremiumEmail(email),
  email: normalizePremiumEmail(email),
  uid: normalizePremiumUid(uid),
  label: String(label || '').trim(),
  active,
  createdAt,
  paidAmount: normalizePremiumAmount(paidAmount),
  paidMonths: Number(paidMonths) || calculatePremiumMonthsFromAmount(paidAmount),
  expiresAt: toDate(expiresAt)?.toISOString() || null,
  lastPaymentAt
})

export const upsertPremiumEntry = (entries = [], nextEntry = {}) => {
  const normalizedNext = normalizePremiumEntry(nextEntry)
  if (!normalizedNext.email && !normalizedNext.uid) return normalizePremiumEntries(entries)

  const nextKey = normalizedNext.uid || normalizedNext.email
  const existingEntries = normalizePremiumEntries(entries)
  const nextEntries = existingEntries.filter(entry => {
    const entryKey = entry.uid || entry.email
    return entryKey !== nextKey && entry.email !== normalizedNext.email && entry.uid !== normalizedNext.uid
  })

  return [...nextEntries, normalizedNext]
}
